import { useParams, Link } from "react-router-dom";
import {
  useIncident,
  useUpdateIncidentStatus,
  useAddIncidentComment,
  useIncidentComments,
  IncidentComment,
} from "@crm/api";
import {
  ArrowLeft,
  Clock,
  AlertCircle,
  CheckCircle,
  MessageSquare,
  Send,
  MapPin,
  Map,
} from "lucide-react";
import { cn } from "@crm/utils";
import { toast } from "sonner";
import { useState, lazy, Suspense } from "react";
import { useForm } from "react-hook-form";
import { IncidentChat } from "../components/IncidentChat";

// Lazy load map component
const LocationMap = lazy(() => import("../../../components/LocationMap"));

// Lazy load images component (uses API hooks)
const IncidentImages = lazy(() => import("../components/IncidentImages").then(m => ({ default: m.IncidentImages })));

const statusConfig: Record<
  string,
  { label: string; color: string; icon: typeof AlertCircle }
> = {
  new: { label: "New", color: "bg-blue-100 text-blue-800", icon: AlertCircle },
  assigned: {
    label: "Assigned",
    color: "bg-yellow-100 text-yellow-800",
    icon: Clock,
  },
  in_progress: {
    label: "In Progress",
    color: "bg-purple-100 text-purple-800",
    icon: Clock,
  },
  on_hold: {
    label: "On Hold",
    color: "bg-gray-100 text-gray-800",
    icon: Clock,
  },
  resolved: {
    label: "Resolved",
    color: "bg-green-100 text-green-800",
    icon: CheckCircle,
  },
  closed: {
    label: "Closed",
    color: "bg-green-100 text-green-800",
    icon: CheckCircle,
  },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low: { label: "Low", color: "bg-gray-100 text-gray-700" },
  medium: { label: "Medium", color: "bg-blue-100 text-blue-700" },
  high: { label: "High", color: "bg-orange-100 text-orange-700" },
  critical: { label: "Critical", color: "bg-red-100 text-red-700" },
};

const categoryLabels: Record<string, string> = {
  hardware: "Hardware",
  software: "Software",
  network: "Network",
  security: "Security",
  access: "Access Request",
  other: "Other",
};

// Quick status transitions for workers
const workerStatusActions = [
  { from: ["new", "assigned"], to: "in_progress", label: "Start Working" },
  { from: ["in_progress"], to: "on_hold", label: "Put on Hold" },
  { from: ["on_hold"], to: "in_progress", label: "Resume" },
  { from: ["in_progress", "on_hold"], to: "resolved", label: "Mark Resolved" },
];

export function IncidentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: incident, isLoading, error } = useIncident(id!);
  const { data: comments } = useIncidentComments(id!);
  const updateStatusMutation = useUpdateIncidentStatus();
  const addCommentMutation = useAddIncidentComment();

  const [showResolveForm, setShowResolveForm] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const { register, handleSubmit, reset } = useForm<{ content: string }>();
  const {
    register: registerResolve,
    handleSubmit: handleResolveSubmit,
    reset: resetResolve,
  } = useForm<{ notes: string }>();

  const handleStatusChange = async (newStatus: string, notes?: string) => {
    if (!id) return;
    try {
      await updateStatusMutation.mutateAsync({ id, status: newStatus, notes });
      toast.success("Status updated");
      setShowResolveForm(false);
      resetResolve();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const onResolve = (data: { notes: string }) => {
    handleStatusChange("resolved", data.notes);
  };

  const onAddComment = async (data: { content: string }) => {
    if (!id || !data.content.trim()) return;
    try {
      await addCommentMutation.mutateAsync({
        incidentId: id,
        payload: { content: data.content },
      });
      toast.success("Comment added");
      reset();
    } catch {
      toast.error("Failed to add comment");
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !incident) {
    return (
      <div className="p-8">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-8 text-center">
          <p className="text-destructive">Failed to load incident</p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to My Incidents
          </Link>
        </div>
      </div>
    );
  }

  const status = statusConfig[incident.status] || statusConfig.new;
  const priority = priorityConfig[incident.priority] || priorityConfig.medium;

  // Get available actions for current status
  const availableActions = workerStatusActions.filter((action) =>
    action.from.includes(incident.status)
  );

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/"
          className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to My Incidents
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-3 py-1 text-sm font-medium",
                  status.color
                )}
              >
                {status.label}
              </span>
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-3 py-1 text-sm font-medium",
                  priority.color
                )}
              >
                {priority.label} Priority
              </span>
            </div>
            <h1 className="mt-3 text-2xl font-semibold text-foreground">
              {incident.title}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              ID: {incident.id.slice(0, 8)} • Category:{" "}
              {categoryLabels[incident.category]}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {availableActions.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {availableActions.map((action) =>
            action.to === "resolved" ? (
              <button
                key={action.to}
                onClick={() => setShowResolveForm(true)}
                className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                disabled={updateStatusMutation.isPending}
              >
                <CheckCircle className="h-4 w-4" />
                {action.label}
              </button>
            ) : (
              <button
                key={action.to}
                onClick={() => handleStatusChange(action.to)}
                className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
                disabled={updateStatusMutation.isPending}
              >
                {action.label}
              </button>
            )
          )}
        </div>
      )}

      {/* Resolve Form */}
      {showResolveForm && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-6">
          <h3 className="mb-4 font-semibold text-green-800">
            Resolve Incident
          </h3>
          <form onSubmit={handleResolveSubmit(onResolve)} className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-green-700">
                Resolution Notes
              </label>
              <textarea
                {...registerResolve("notes")}
                rows={3}
                className="w-full rounded-md border border-green-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Describe how the issue was resolved..."
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                disabled={updateStatusMutation.isPending}
              >
                Confirm Resolution
              </button>
              <button
                type="button"
                onClick={() => setShowResolveForm(false)}
                className="rounded-md border border-green-300 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-100"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              Description
            </h2>
            <p className="whitespace-pre-wrap text-foreground">
              {incident.description || "No description provided."}
            </p>
          </div>

          {/* Resolution Notes */}
          {incident.resolution_notes && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-6">
              <h2 className="mb-4 text-lg font-semibold text-green-800">
                Resolution Notes
              </h2>
              <p className="whitespace-pre-wrap text-green-700">
                {incident.resolution_notes}
              </p>
            </div>
          )}

          {/* Work Images */}
          <Suspense
            fallback={
              <div className="h-48 rounded-lg border border-border bg-card flex items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            }
          >
            <IncidentImages incidentId={id!} />
          </Suspense>

          {/* Comments */}
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
              <MessageSquare className="h-5 w-5" />
              Comments
            </h2>

            {/* Add comment form */}
            <form onSubmit={handleSubmit(onAddComment)} className="mb-6">
              <div className="flex gap-2">
                <input
                  {...register("content")}
                  type="text"
                  placeholder="Add a comment..."
                  className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  disabled={addCommentMutation.isPending}
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </form>

            {/* Comments list */}
            {comments?.length ? (
              <div className="space-y-4">
                {comments.map((comment: IncidentComment) => (
                  <div
                    key={comment.id}
                    className="border-l-2 border-muted pl-4"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-foreground">
                        {comment.author?.first_name} {comment.author?.last_name}
                      </span>
                      <span className="text-muted-foreground">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-foreground">
                      {comment.content}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No comments yet.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Details */}
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="mb-4 text-sm font-semibold text-foreground">
              Details
            </h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Category</dt>
                <dd className="font-medium text-foreground">
                  {categoryLabels[incident.category] || incident.category}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Created</dt>
                <dd className="font-medium text-foreground">
                  {new Date(incident.created_at).toLocaleString()}
                </dd>
              </div>
              {incident.assigned_at && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Assigned</dt>
                  <dd className="font-medium text-foreground">
                    {new Date(incident.assigned_at).toLocaleString()}
                  </dd>
                </div>
              )}
              {incident.resolved_at && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Resolved</dt>
                  <dd className="font-medium text-foreground">
                    {new Date(incident.resolved_at).toLocaleString()}
                  </dd>
                </div>
              )}
              {incident.client_company && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Client</dt>
                  <dd className="font-medium text-foreground">
                    {incident.client_company.name}
                  </dd>
                </div>
              )}
              {incident.client_contact && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Contact</dt>
                  <dd className="font-medium text-foreground">
                    {incident.client_contact.first_name}{" "}
                    {incident.client_contact.last_name}
                  </dd>
                </div>
              )}
              {(incident.location_address || incident.location_latitude) && (
                <div className="pt-2 border-t border-border">
                  <dt className="text-muted-foreground flex items-center gap-1 mb-1">
                    <MapPin className="h-3 w-3" />
                    Location
                  </dt>
                  <dd className="text-sm">
                    {incident.location_address && (
                      <p className="font-medium text-foreground">
                        {incident.location_address}
                      </p>
                    )}
                    {incident.location_latitude &&
                      incident.location_longitude && (
                        <>
                          <p className="text-xs text-muted-foreground">
                            {Number(incident.location_latitude).toFixed(6)},{" "}
                            {Number(incident.location_longitude).toFixed(6)}
                          </p>
                          <button
                            onClick={() => setShowMap(!showMap)}
                            className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <Map className="h-3 w-3" />
                            {showMap ? "Hide Map" : "View on Map"}
                          </button>
                        </>
                      )}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Location Map */}
          {showMap &&
            incident.location_latitude &&
            incident.location_longitude && (
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <div className="border-b border-border bg-muted/50 px-4 py-2">
                  <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Map className="h-4 w-4" />
                    Incident Location
                  </h3>
                </div>
                <Suspense
                  fallback={
                    <div className="h-[250px] flex items-center justify-center bg-muted/20">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                  }
                >
                  <LocationMap
                    latitude={Number(incident.location_latitude)}
                    longitude={Number(incident.location_longitude)}
                    address={incident.location_address || undefined}
                    title="Incident Location"
                    className="h-[250px] w-full"
                  />
                </Suspense>
              </div>
            )}

          {/* Reported By */}
          {incident.reported_by && (
            <div className="rounded-lg border border-border bg-card p-6">
              <h3 className="mb-4 text-sm font-semibold text-foreground">
                Reported By
              </h3>
              <p className="font-medium text-foreground">
                {incident.reported_by.first_name}{" "}
                {incident.reported_by.last_name}
              </p>
              <p className="text-sm text-muted-foreground">
                {incident.reported_by.email}
              </p>
            </div>
          )}

          {/* Real-time Chat */}
          <IncidentChat incidentId={id!} />
        </div>
      </div>
    </div>
  );
}
